import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';

import TagPill from './Elements/TagPill';

interface SelectionsGridProps {
  selections?: any[];
  onSelectSuggestion: (label: string) => void;
  onEditTag?: (tag: any) => void;
  onDeleteTag?: (tag: any) => void;
}

const SelectionsGrid: React.FC<SelectionsGridProps> = ({
  selections,
  onSelectSuggestion,
  onEditTag,
  onDeleteTag,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [pillsPerRow, setPillsPerRow] = useState(3);
  const pillMinWidth = 60;
  const pillGap = 8;
  const maxInitialRows = 2;
  const maxRows = 5;
  const [showAllRows, setShowAllRows] = useState(false);

  // Update pillsPerRow based on container width
  useEffect(() => {
    function updatePillsPerRow() {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const pillWidthWithGap = pillMinWidth + pillGap;
        const perRow = Math.max(1, Math.floor(containerWidth / pillWidthWithGap));
        setPillsPerRow(perRow);
      }
    }
    updatePillsPerRow();
    window.addEventListener('resize', updatePillsPerRow);
    return () => window.removeEventListener('resize', updatePillsPerRow);
  }, []);

  const totalRows =
    selections && selections.length ? Math.ceil(selections.length / pillsPerRow) : 0;

  const handleEditTag = (tag: any) => {
    if (onEditTag) onEditTag(tag);
  };

  const handleDeleteTag = (tag: any) => {
    if (onDeleteTag) onDeleteTag(tag);
  };

  if (!selections || selections.length === 0) {
    return (
      <div className={clsx('tw-flex', 'tw-flex-col', 'tw-gap-y-4')}>
        <span className="tw-text-sm">Selection: </span>
        <span className={clsx('tw-text-sm', 'tw-text-gray-400', 'tw-text-center', 'tw-w-full')}>
          No Tag available.
        </span>
      </div>
    );
  }
  const visibleRows = showAllRows ? totalRows : Math.min(maxInitialRows, totalRows);
  const visibleSelections = showAllRows
    ? selections
    : selections.slice(0, visibleRows * pillsPerRow);

  return (
    <div className={clsx('tw-flex', 'tw-flex-col', 'tw-gap-y-4')}>
      <span className="tw-text-sm">Selection: </span>
      <div
        ref={containerRef}
        className={clsx(
          'tw-flex',
          'tw-flex-wrap',
          'tw-gap-2',
          totalRows > maxRows ? 'tw-max-h-[140px] tw-overflow-y-auto' : '',
          'tw-transition-all',
          'tw-w-full'
        )}
        style={{
          flexWrap: 'wrap',
          gap: '8px',
          width: '100%',
        }}
      >
        {visibleSelections.map((s) => (
          <TagPill
            key={s.name}
            tag={s}
            onClick={() => onSelectSuggestion(s.name)}
            onEdit={() => handleEditTag(s)}
            onDelete={() => handleDeleteTag(s)}
            removable={true}
            className={clsx(
              'tw-px-3',
              'tw-py-1',
              'tw-rounded-full',
              'tw-text-sm',
              'tw-flex',
              'tw-items-center',
              'tw-gap-1',
              s.color
            )}
            style={{
              ...(s.color ? { backgroundColor: s.color } : {}),
              minWidth: 60,
              maxWidth: 90,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: '0 1 auto',
            }}
            isFromSelectionsGrid={true}
          />
        ))}
        {/* View More button if more than 2 rows and not expanded */}
        {!showAllRows && selections.length > visibleSelections.length && (
          <button
            className={clsx(
              'tw-col-span-3',
              'tw-px-2',
              'tw-py-1',
              'tw-bg-cfo-grey-200',
              'tw-rounded-full',
              'tw-text-sm',
              'tw-mt-2',
              'tw-w-full',
              'tw-text-center',
              'hover:tw-bg-cfo-blue-100'
            )}
            onClick={() => setShowAllRows(true)}
          >
            View More
          </button>
        )}
      </div>
    </div>
  );
};

export default SelectionsGrid;
